import * as React from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import NotificationPreferences from "../../components/NotificationPreferences";
import PhoneInput from "../../components/PhoneInput";
import { clearAuthSession, getAuthSession } from "../../services/auth/auth-session";
import { fetchMyProfile, removeMyAccount, updateMyProfile } from "../../services/auth/profile-api";
import { isValidE164Phone } from "../../utils/phone-utils";
import { EMAIL_REGEX, NAME_REGEX } from "../../config/validation";

const hasScope = (scopes, scopeName) => scopes.includes(scopeName);

const shouldRedirectForAuthError = (message) => {
  if (!message) return false;
  return (
    message.includes("Authorization bearer token is required") ||
    message.includes("Session is not active") ||
    message.includes("Authenticated user does not have an email claim")
  );
};

export default function ProfileView() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // /profile?tab=notifications opens the notification settings (used by links from the unsubscribe page).
  const tab = searchParams.get("tab") === "notifications" ? "notifications" : "profile";
  const onTabChange = (_event, value) =>
    setSearchParams(value === "profile" ? {} : { tab: value }, { replace: true });
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [form, setForm] = useState({
    email: "",
    phoneE164: "",
    firstName: "",
    lastName: "",
    displayName: "",
    picture: "",
    scopes: [],
  });

  const avatarLabel = useMemo(
    () => form.displayName || form.firstName || form.email || "Profile",
    [form.displayName, form.email, form.firstName],
  );

  useEffect(() => {
    fetchMyProfile()
      .then((profile) => {
        if (!hasScope(profile.scopes || [], "profile")) {
          navigate("/", { replace: true });
          return;
        }
        setForm({
          email: profile.email || "",
          phoneE164: profile.phone || "",
          firstName: profile.firstName || "",
          lastName: profile.lastName || "",
          displayName: profile.displayName || "",
          picture: profile.picture || "",
          scopes: profile.scopes || [],
        });
      })
      .catch((error) => {
        const message = error?.message || "Failed to load profile.";
        if (shouldRedirectForAuthError(message)) {
          clearAuthSession();
          navigate("/", { replace: true });
          return;
        }
        setErrorMessage(message);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [navigate]);

  const onUploadProfilePicture = () => {
    fileInputRef.current?.click();
  };

  const onFileChange = (event) => {
    const selected = event.target.files?.[0];
    if (!selected) return;
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") {
        setForm((current) => ({ ...current, picture: reader.result }));
      }
    };
    reader.readAsDataURL(selected);
  };

  const onSave = async () => {
    setSaving(true);
    setErrorMessage("");
    setSuccessMessage("");

    const normalizedEmail = form.email.trim().toLowerCase();
    if (!EMAIL_REGEX.test(normalizedEmail)) {
      setErrorMessage("Please enter a valid email address.");
      setSaving(false);
      return;
    }

    const trimmedFirstName = form.firstName.trim();
    const trimmedLastName = form.lastName.trim();

    if (trimmedFirstName && !NAME_REGEX.test(trimmedFirstName)) {
      setErrorMessage("First name can only contain letters, spaces, hyphens, and apostrophes.");
      setSaving(false);
      return;
    }

    if (trimmedLastName && !NAME_REGEX.test(trimmedLastName)) {
      setErrorMessage("Last name can only contain letters, spaces, hyphens, and apostrophes.");
      setSaving(false);
      return;
    }

    if (!isValidE164Phone(form.phoneE164)) {
      setErrorMessage("Please enter a valid phone number.");
      setSaving(false);
      return;
    }

    try {
      const updated = await updateMyProfile({
        email: normalizedEmail,
        firstName: trimmedFirstName,
        lastName: trimmedLastName,
        picture: form.picture,
        phone: form.phoneE164,
      });
      setForm((current) => ({
        ...current,
        email: updated.email || current.email,
        phoneE164: updated.phone || current.phoneE164,
        firstName: updated.firstName || current.firstName,
        lastName: updated.lastName || current.lastName,
        displayName: updated.displayName || current.displayName,
        picture: updated.picture || current.picture,
      }));
      setSuccessMessage("Profile updated.");
    } catch (error) {
      const message = error?.message || "Failed to update profile.";
      if (shouldRedirectForAuthError(message)) {
        clearAuthSession();
        navigate("/", { replace: true });
        return;
      }
      setErrorMessage(message);
    } finally {
      setSaving(false);
    }
  };

  const onDeleteAccount = async () => {
    setSaving(true);
    setErrorMessage("");
    try {
      await removeMyAccount();
      clearAuthSession();
      navigate("/", { replace: true });
    } catch (error) {
      const message = error?.message || "Failed to remove account.";
      if (shouldRedirectForAuthError(message)) {
        clearAuthSession();
        navigate("/", { replace: true });
        return;
      }
      setErrorMessage(message);
    } finally {
      setSaving(false);
      setDeleteOpen(false);
    }
  };

  const profileScopeEnabled = hasScope(form.scopes, "profile");
  const emailScopeEnabled = hasScope(form.scopes, "email");

  return (
    <Box sx={{ px: 3, py: 4 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Profile
      </Typography>

      {errorMessage ? <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert> : null}
      {successMessage ? <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert> : null}

      <Tabs value={tab} onChange={onTabChange} sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}>
        <Tab value="profile" label="Profile" />
        <Tab value="notifications" label="Notifications" />
      </Tabs>

      {tab === "notifications" ? (
        <NotificationPreferences />
      ) : loading ? (
        <Typography variant="body2">Loading profile...</Typography>
      ) : (
        <Stack direction={{ xs: "column", md: "row" }} spacing={4} alignItems="flex-start">
          <Stack spacing={2} alignItems="center" sx={{ minWidth: 220 }}>
            <Avatar src={form.picture || undefined} alt={avatarLabel} sx={{ width: 120, height: 120 }}>
              {avatarLabel[0]?.toUpperCase()}
            </Avatar>
            <Button variant="text" onClick={onUploadProfilePicture}>
              Upload profile picture
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={onFileChange}
            />
          </Stack>

          <Stack spacing={2} sx={{ width: "100%", maxWidth: 700 }}>
            {profileScopeEnabled ? (
              <>
                <Typography variant="caption" color="text.secondary">
                  {(() => {
                    const p = getAuthSession()?.provider ?? "google";
                    return `Originally pulled from your ${p.charAt(0).toUpperCase() + p.slice(1)} account`;
                  })()}
                </Typography>
                <TextField
                  label="First name"
                  value={form.firstName}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, firstName: event.target.value }))
                  }
                  fullWidth
                />
                <TextField
                  label="Last name"
                  value={form.lastName}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, lastName: event.target.value }))
                  }
                  fullWidth
                />
              </>
            ) : null}

            {emailScopeEnabled ? (
              <TextField
                label="Email"
                value={form.email}
                onChange={(event) =>
                  setForm((current) => ({ ...current, email: event.target.value }))
                }
                fullWidth
              />
            ) : null}

            <PhoneInput
              value={form.phoneE164}
              onChange={(e164) => setForm((current) => ({ ...current, phoneE164: e164 }))}
              disabled={saving}
            />

            <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
              <Button variant="contained" onClick={onSave} disabled={saving}>
                Save
              </Button>
            </Stack>

            <Box sx={{ pt: 2 }}>
              <Button
                color="secondary"
                variant="outlined"
                onClick={() => setDeleteOpen(true)}
                disabled={saving}
              >
                Remove account
              </Button>
            </Box>
          </Stack>
        </Stack>
      )}

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Remove account?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This permanently removes your account and related roster data from Puck-Pickup.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button color="secondary" onClick={onDeleteAccount}>
            Remove account
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
